import React from 'react';
import classnames from 'classnames';
import { ISection } from '../types';
import { Typography } from '@design-system/components/typography/typography';
import { Modal } from '@design-system/components/modal/modal';

export type Option = {
  value: ISection['type'];
  label: string;
  image?: string;
  video?: string;
  category?: string;
};

type BlockSelectModalProps = {
  onSelect: (o: ISection['type']) => void;
  onClose: () => void;
  isOpen: boolean;
  tabs: Array<{ title: string; value: Option[] }>;
};

export const BlockSelectModal = ({ ...p }: BlockSelectModalProps) => {
  const [selectedOption, onSelectOption] = React.useState<null | Option>(null);

  // Reset selection when modal opens
  React.useEffect(() => {
    if (p.isOpen) {
      onSelectOption(null);
    }
  }, [p.isOpen]);

  const handleSelect = (o: Option | null) => {
    onSelectOption(o);
    if (!o) return;
    p.onSelect(o.value);
    p.onClose();
  };

  const handleOptionSelect = (o: Option | null) => {
    onSelectOption(o);
  };
  return (
    <Modal
      isOpen={p.isOpen}
      onClose={p.onClose}
      stretchCta
      primaryCta={{
        caption: 'Insert',
        disabled: selectedOption === null,
        onClick: () => handleSelect(selectedOption),
      }}
      modalClassName="w-full md:w-[1000px]"
      centerContent
      title="Add new section"
    >
      <div className="flex flex-col gap-6">
        <SelectBlock
          value={selectedOption}
          onSelect={handleOptionSelect}
          tabs={p.tabs}
        />
      </div>
    </Modal>
  );
};

type SelectBlockProps = {
  value: Option | null;
  onSelect: (o: Option | null) => void;
} & Pick<BlockSelectModalProps, 'tabs'>;

function SelectBlock(p: SelectBlockProps) {
  const scrollContainerRef = React.useRef<HTMLElement>(null);
  const [showGradient, setShowGradient] = React.useState(false);

  // Flatten all options from all tabs into a single array with category
  const allOptions: Option[] = p.tabs.flatMap((tab) =>
    tab.value.map((option) => ({
      ...option,
      category: tab.title,
    })),
  );

  // Check if content is scrollable and if we're at the bottom
  const checkScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 1; // Small threshold for rounding

    // Show gradient only if scrollable AND not at bottom
    setShowGradient(isScrollable && !isAtBottom);
  };

  // Check on mount and when options change
  React.useEffect(() => {
    checkScrollState();
  }, [allOptions.length]);

  // Handle scroll events
  const handleScroll = () => {
    checkScrollState();
  };

  return (
    <div className="w-full">
      <div style={{ position: 'relative' }}>
        <section
          ref={scrollContainerRef}
          className="flex flex-wrap gap-6 overflow-y-auto max-h-[calc(80vh-200px)] pr-2"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1',
          }}
          onScroll={handleScroll}
        >
          {allOptions.map((o) => (
            <BlockOption
              o={o}
              value={p.value}
              onSelect={p.onSelect}
              key={`${o.category}-${o.label}`}
              isSelected={o.label === p.value?.label}
            />
          ))}
        </section>
        {/* Gradient overlay at the bottom to indicate scrollability */}
        {showGradient && (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-16"
            style={{
              background:
                'linear-gradient(to top, rgba(255,255,255,0.9), transparent)',
            }}
          />
        )}
      </div>
    </div>
  );
}
type BlockOptionProps = {
  o: Option;
  onSelect: (o: Option) => void;
  isSelected: boolean;
  value: Option | null;
};

function BlockOption({ o, onSelect, value, ...p }: BlockOptionProps) {
  const handleClick = () => {
    onSelect(o);
  };

  return (
    <div
      key={o.value}
      onClick={handleClick}
      className="cursor-pointer"
      style={{
        padding: '1em',
        textAlign: 'center',
        borderRadius: '10px',
        ...(p.isSelected ? { background: '#eee' } : {}),
      }}
    >
      <Typography variant="bodyLarge">{o.label}</Typography>
      <div
        className={classnames(
          'h-[200px] w-[260px] flex items-center justify-center border rounded-lg border-gray-200 bg-white relative',
        )}
        style={{ marginTop: '1em' }}
      >
        {o.image ? (
          <img
            className={classnames('cursor-pointer max-h-[160px] w-auto')}
            src={o.image}
            alt={o.label}
          />
        ) : (
          <h2>{o.label}</h2>
        )}
      </div>
    </div>
  );
}
